import './InvalidRotationsReport.scss';
import cn from 'classnames';
import * as Dfns from 'date-fns/fp';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Collapse, Form, Modal } from 'react-bootstrap';
import { MdChevronRight, MdExpandLess, MdExpandMore } from 'react-icons/md';
import { Button } from '@Components/Button';
import { Appearance, Intent, Size } from '@Components/designConstants';
import { FilterBar } from '@Components/FilterBar';
import { Img } from '@Components/Img';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { getCreativeThumbnail } from '@Components/MetricsTable/metrics-table-utils';
import { FullPageSpinner } from '@Components/Spinner/Spinner';
import { useAppSelector } from '../../redux';
import { currentCompanySelector } from '../../redux/company';
import { useSetAreYouSure, useSetError } from '../../redux/modals';
import { awaitJSON, CreativeLambdaFetch } from '../../utils/fetch-utils';
import { useStateFunction } from '../../utils/hooks/useData';
import {
  PRETTY_DATE_FORMAT,
  RotationStatusType,
  Urgency,
  URGENCY_META,
  UrgencyType
} from './invalid-rotations-constants';
import {
  formatAdServer,
  getIsImminent,
  getRelativeBadDay,
  TODAY,
  toPrettyDate
} from './invalid-rotations-utils';

const FILTER_BAR_OPTIONS = [
  { name: 'date', label: 'Bad Day' },
  { name: 'isci', label: 'ISCI' },
  { name: 'network', label: 'Network' },
  { name: 'platform', label: 'Platform' },
  { name: 'placementName', label: 'Placement Name' },
  { name: 'placementID', label: 'Placement ID' },
  { name: 'suppressed', label: 'Is Suppressed' },
  { name: 'suppressedUntil', label: 'Suppressed Until' },
  { name: 'suppressedBy', label: 'Suppressed By' },
  { name: 'rotationStart', label: 'Rotation Start Date' },
  { name: 'rotationEnd', label: 'Rotation End Date' },
  { name: 'flightStart', label: 'Flight Start Date' },
  { name: 'flightEnd', label: 'Flight End Date' },
  { name: 'lastLiveStart', label: 'Last Live Start Date' },
  { name: 'lastLiveEnd', label: 'Last Live End Date' },
  { name: 'flightID', label: 'Flight ID' },
  { name: 'orderID', label: 'Order ID' }
];

// Bucket a rotation by how soon its bad day is. Returns the Urgency constant directly so the
// grouping stays tied to the enum (a renamed value can't silently drift from the SCSS classes).
const getUrgency = (date: string): UrgencyType => {
  if (date === TODAY) return Urgency.TODAY;
  if (getIsImminent(date)) return Urgency.IMMINENT;
  return Urgency.OK;
};

interface Flight {
  id: string;
  order_id: string;
  start_date: string;
  end_date: string;
}

interface DateRange {
  start_date?: string;
  end_date?: string;
}

interface Suppression {
  suppress_until: string;
  lastuser: string;
  lastmodified: string;
  because: string;
}

interface RotationLine {
  placementID: string;
  isci: string;
  pct: number;
  rotationDates: DateRange;
  flight: Flight;
  lastLiveDates: DateRange;
  suppression?: Suppression;
  date: string;
  network: string;
  platform: string;
  placementName: string;
  adServer?: string;
}

interface ReportData {
  problems: RotationLine[];
  suppressions: RotationLine[];
}

interface PlacementSection {
  network: string;
  platform: string;
  placementName: string;
  placementID: string;
  adServer?: string;
  lines: Pick<
    RotationLine,
    'isci' | 'date' | 'pct' | 'rotationDates' | 'flight' | 'lastLiveDates'
  >[];
}

const DateSet = ({ start, end }: { start?: string; end?: string }) => {
  if (!start) return <div>None</div>;
  return (
    <div className='date-set'>
      {toPrettyDate(start)} – {end ? toPrettyDate(end) : 'Forever'}
    </div>
  );
};

interface ProblemPaneProps {
  data: RotationLine;
  resetData: () => void;
}

const ProblemPane = ({ data, resetData }: ProblemPaneProps) => {
  const {
    network,
    platform,
    placementName,
    placementID,
    date,
    isci,
    rotationDates,
    flight,
    lastLiveDates = {},
    suppression
  } = data;
  const company = useAppSelector(currentCompanySelector);

  const setError = useSetError();
  const setAreYouSure = useSetAreYouSure();

  const [suppressDays, setSuppressDays] = useState(1);
  const [because, setBecause] = useState('');

  const imminentDanger = useMemo(() => getIsImminent(date), [date]);

  const [suppressing, setSuppressing] = useState(false);
  const suppress = useCallback(async () => {
    setSuppressing(true);
    try {
      await setAreYouSure({
        title: 'Suppressing Slack Alert',
        message: `You're about to suppress slack alerts for isci ${isci} on placement ${placementID} for ${suppressDays} day${
          suppressDays === 1 ? '' : 's'
        }. Are you sure you want to do this?`,
        okayText: 'Suppress Alerts'
      });
    } catch {
      setSuppressing(false);
      return;
    }
    if (!because) {
      try {
        await setAreYouSure({
          title: 'Suppressing Without Explanation',
          message:
            'You are about to suppress a slack alert without giving an explanation. Explanations are important for knowing why an alert was suppressed. Are you sure you want to continue?',
          okayText: 'Suppress Without Explanation'
        });
      } catch {
        setSuppressing(false);
        return;
      }
    }
    if (imminentDanger) {
      try {
        await setAreYouSure({
          intent: Intent.ERROR,
          title: 'Are you Sure?',
          message: `This ISCI will run ${
            date === TODAY ? 'TODAY' : 'very soon'
          }. Unless you are certain that this isn't an actual issue, you should either get the correct rotations into the system or update the creative map. Are you sure you want to suppress the alert?`,
          okayText: 'Suppress Anyway'
        });
      } catch {
        setSuppressing(false);
        return;
      }
    }

    try {
      await CreativeLambdaFetch('/suppress_rotation_warning', {
        method: 'post',
        body: {
          placementID,
          isci,
          days: suppressDays,
          because
        }
      });
      resetData();
    } catch (e) {
      await setError({
        message: `Failed to suppress slack alert. Error: ${e.message}`,
        reportError: e
      });
    }
  }, [
    setAreYouSure,
    setError,
    suppressDays,
    isci,
    placementID,
    because,
    resetData,
    imminentDanger,
    date
  ]);

  const suppressionSubmitDate = suppression
    ? Dfns.format('M/dd/yyyy @ h:mma')(Dfns.parseISO(suppression.lastmodified))
    : undefined;

  return (
    <div className='problem-view'>
      <div className='info-section'>
        <div>
          <div>Network:</div>
          <div>
            {network} {platform}
          </div>
        </div>
        <div>
          <div>Placement:</div>
          <div>
            {placementName} (#{placementID})
          </div>
        </div>
        <div>
          <div>ISCI:</div>
          <div>{isci}</div>
        </div>
      </div>
      <Alert variant={imminentDanger ? 'danger' : 'warning'}>
        This ISCI{' '}
        {date === TODAY ? (
          <strong>IS RUNNING TODAY</strong>
        ) : (
          <>
            will run on <strong>{toPrettyDate(date)}</strong>
          </>
        )}
        {date === TODAY
          ? '. It is not marked as live in the creative map.'
          : '. It is not marked as live in the creative map on that date.'}
      </Alert>
      <div className='creative-row'>
        <div className='img-box'>
          <Img
            title={isci}
            src={getCreativeThumbnail(company, isci)}
            className='thumbnail'
            unloader={<div className='thumbnail' />}
          />
        </div>
        <div className='date-box'>
          <div className='section-header'>Last Live Dates</div>
          {lastLiveDates.start_date ? (
            <DateSet start={lastLiveDates.start_date} end={lastLiveDates.end_date} />
          ) : (
            <div>Never Live</div>
          )}
          <a className='fix-link' target='_blank' rel='noreferrer' href={`/${company}/creatives`}>
            Fix Creative Map
          </a>
          <div className='section-header'>Rotation Run Dates</div>
          <DateSet start={rotationDates.start_date} end={rotationDates.end_date} />
          <a
            className='fix-link'
            target='_blank'
            rel='noreferrer'
            href={`/${company}/streaming/creatives`}
          >
            Fix Streaming Creative Rotations
          </a>
        </div>
      </div>
      <div className='flight-row'>
        <div className='section-header'>Next Flight</div>
        <div className='flight-info'>
          <div>Flight ID #{flight.id}</div>
          <div>Order ID #{flight.order_id}</div>
          <DateSet start={flight.start_date} end={flight.end_date} />
        </div>
      </div>
      <hr />
      {suppression ? (
        <div>
          <Alert variant='info'>
            This alert is suppressed until {toPrettyDate(suppression.suppress_until)}
          </Alert>
          <div>
            This alert was suppressed by <code>{suppression.lastuser}</code> on{' '}
            <code>{suppressionSubmitDate}</code>. Reason:
          </div>
          <div className='because-box'>{suppression.because}</div>
        </div>
      ) : (
        <div>
          <div className='suppress-days'>
            Suppress slack alert for{' '}
            <Form.Control
              className='suppress-days-control'
              size='sm'
              type='number'
              min={0}
              value={suppressDays}
              onChange={e => setSuppressDays(Number(e.target.value))}
            />{' '}
            day{suppressDays === 1 ? '' : 's'} (until{' '}
            {Dfns.format(PRETTY_DATE_FORMAT)(Dfns.addDays(suppressDays)(new Date()))}) because
          </div>
          <Form.Control
            className='because-input'
            as='textarea'
            rows={3}
            value={because}
            onChange={e => setBecause(e.target.value)}
          />
          <Button
            size={Size.LG}
            className='w-100'
            design={imminentDanger ? Intent.ERROR : Intent.WARNING}
            disabled={suppressing}
            onClick={suppress}
          >
            Suppress Slack Alerts
          </Button>
        </div>
      )}
    </div>
  );
};

export const InvalidRotationsReport = () => {
  const setError = useSetError();
  const company = useAppSelector(currentCompanySelector);

  const [data, setData] = useState<ReportData>();

  useEffect(() => {
    if (!data) {
      (async () => {
        try {
          const res = await CreativeLambdaFetch('/check_invalid', {
            params: { company }
          });
          setData(await awaitJSON<ReportData>(res));
        } catch (e) {
          const message = `Could not fetch invalid rotations report. Error: ${e.message}.`;
          setError({ message, reportError: e });
        }
      })();
    }
  }, [data, company, setError]);

  const [filter, setFilter] = useStateFunction<(line: object) => boolean>(() => true);

  const { problems, suppressions, rotationMap, filterBarLines } = useMemo(() => {
    const allLines = Object.values(data ?? {}).flat();
    const rotMap = allLines.reduce<Record<string, RotationLine>>(
      (agg, elem) => ({ ...agg, [`${elem.placementID}_${elem.isci}`]: elem }),
      {}
    );

    const fbarLines: object[] = [];

    const processSection = (
      lines: RotationLine[]
    ): Partial<Record<UrgencyType, PlacementSection[]>> => {
      // One pass: record every line for the filter bar, and keep the ones passing the active filter.
      const filtered: RotationLine[] = [];
      lines.forEach(line => {
        const { rotationDates, flight, lastLiveDates, suppression, date, ...restLine } = line;
        const filterableLine = {
          ...restLine,
          date: date === TODAY ? 'Today' : date,
          flightID: flight.id,
          orderID: flight.order_id,
          flightStart: flight.start_date,
          flightEnd: flight.end_date,
          rotationStart: rotationDates.start_date,
          rotationEnd: rotationDates.end_date,
          lastLiveStart: lastLiveDates?.start_date,
          lastLiveEnd: lastLiveDates?.end_date,
          suppressed: Boolean(suppression),
          suppressedUntil: suppression?.suppress_until,
          suppressedBy: suppression?.lastuser
        };
        fbarLines.push(filterableLine);
        if (filter(filterableLine)) {
          filtered.push(line);
        }
      });

      const grouped: Partial<Record<UrgencyType, RotationLine[]>> = {};
      filtered.forEach(line => {
        const urgency = getUrgency(line.date);
        (grouped[urgency] ??= []).push(line);
      });

      const result: Partial<Record<UrgencyType, PlacementSection[]>> = {};
      for (const urgency of Object.keys(grouped) as UrgencyType[]) {
        const byPlacement: Record<string, RotationLine[]> = {};
        (grouped[urgency] ?? []).forEach(line => {
          (byPlacement[line.placementID] ??= []).push(line);
        });
        result[urgency] = Object.values(byPlacement)
          .map((placementLines: RotationLine[]) => {
            const { network, platform, placementName, placementID, adServer } = placementLines[0];
            return {
              network,
              platform,
              placementName,
              placementID,
              adServer,
              lines: placementLines
                .map(({ isci, date, pct, rotationDates, flight, lastLiveDates }) => ({
                  isci,
                  date,
                  pct,
                  rotationDates,
                  flight,
                  lastLiveDates
                }))
                .sort((a, b) => a.isci.localeCompare(b.isci))
            };
          })
          .sort((a, b) => {
            const keyA = [a.network, a.platform, a.placementName].join('_');
            const keyB = [b.network, b.platform, b.placementName].join('_');
            return keyA.localeCompare(keyB);
          });
      }
      return result;
    };

    const probsResult = processSection(data?.problems ?? []);
    const suppsResult = processSection(data?.suppressions ?? []);

    return {
      problems: probsResult,
      suppressions: Object.values(suppsResult).flatMap(sections => sections ?? []),
      rotationMap: rotMap,
      filterBarLines: fbarLines
    };
  }, [data, filter]);

  const [selectedRotation, setSelectedRotation] = useState<string>();

  const selectedRotationData = selectedRotation ? rotationMap[selectedRotation] : undefined;

  const [showExplainerModal, setShowExplainerModal] = useState(false);
  const [showAllFilteredModal, setShowAllFilteredModal] = useState(false);

  const allFilteredProblems = useMemo(() => {
    if (!problems) return [];
    const filteredProblems: { placementID: string; isci: string }[] = [];
    for (const sectionArray of Object.values(problems)) {
      for (const { placementID, lines } of sectionArray ?? []) {
        for (const { isci } of lines ?? []) {
          filteredProblems.push({ placementID, isci });
        }
      }
    }
    return filteredProblems;
  }, [problems]);

  const isFiltered = useMemo(() => {
    const dataLength = (data?.problems?.length ?? 0) + (data?.suppressions?.length ?? 0);
    return allFilteredProblems.length + suppressions.length < dataLength;
  }, [data, allFilteredProblems, suppressions]);

  if (!data) return <FullPageSpinner />;

  return (
    <div className='invalid-rotations-report'>
      <div className='rotation-list-view'>
        <div>
          <Button
            appearance={Appearance.EMPTY}
            size={Size.SM}
            onClick={() => setShowExplainerModal(true)}
          >
            What is this report about?
          </Button>
        </div>
        <div className='filter-bar-section'>
          <FilterBar
            size={Size.LG}
            lines={filterBarLines}
            onFilter={setFilter}
            options={FILTER_BAR_OPTIONS}
          />
          <Button
            size={Size.LG}
            appearance={Appearance.OUTLINED}
            disabled={!allFilteredProblems.length}
            onClick={() => setShowAllFilteredModal(true)}
          >
            Suppress All Filtered
          </Button>
        </div>
        <RotationListGroups
          problems={problems}
          suppressions={suppressions}
          selectedRotation={selectedRotation}
          onSelectRotation={setSelectedRotation}
        />
      </div>
      {selectedRotationData && (
        <ProblemPane data={selectedRotationData} resetData={() => setData(undefined)} />
      )}
      {showExplainerModal && <ExplainerModal onClose={() => setShowExplainerModal(false)} />}
      {showAllFilteredModal && (
        <SuppressAllFilteredModal
          onClose={() => setShowAllFilteredModal(false)}
          lines={allFilteredProblems}
          isFiltered={isFiltered}
          resetData={() => setData(undefined)}
        />
      )}
    </div>
  );
};

interface RotationListGroupsProps {
  problems: Partial<Record<UrgencyType, PlacementSection[]>>;
  suppressions: PlacementSection[];
  selectedRotation?: string;
  onSelectRotation: (key: string | undefined) => void;
}

const RotationListGroups = ({
  problems,
  suppressions,
  selectedRotation,
  onSelectRotation
}: RotationListGroupsProps) => {
  const sections: { urgency: UrgencyType; placements: PlacementSection[] }[] = [
    { urgency: Urgency.TODAY, placements: problems[Urgency.TODAY] ?? [] },
    { urgency: Urgency.IMMINENT, placements: problems[Urgency.IMMINENT] ?? [] },
    { urgency: Urgency.OK, placements: problems[Urgency.OK] ?? [] },
    { urgency: Urgency.SUPPRESSED, placements: suppressions }
  ];

  if (!sections.some(({ placements }) => placements.length))
    return <div className='empty-state'>No invalid rotations match your filter.</div>;

  return (
    <div className='rotation-list'>
      {sections.map(({ urgency, placements }) => (
        <UrgencySection
          key={urgency}
          urgency={urgency}
          placements={placements}
          selectedRotation={selectedRotation}
          onSelectRotation={onSelectRotation}
        />
      ))}
    </div>
  );
};

interface UrgencySectionProps {
  urgency: UrgencyType;
  placements: PlacementSection[];
  selectedRotation?: string;
  onSelectRotation: (key: string | undefined) => void;
}

const UrgencySection = ({
  urgency,
  placements,
  selectedRotation,
  onSelectRotation
}: UrgencySectionProps) => {
  const { label, status } = URGENCY_META[urgency];
  // Smart default: urgent buckets start open, the rest collapsed.
  const [open, setOpen] = useState(urgency === Urgency.TODAY || urgency === Urgency.IMMINENT);

  if (!placements.length) return null;

  const rotationCount = placements.reduce((sum, { lines }) => sum + lines.length, 0);

  return (
    <div className='urgency-section'>
      <InteractiveDiv className='urgency-header' onClick={() => setOpen(prev => !prev)}>
        <span className={cn('status-dot', `status-${status}`)} />
        <span className='urgency-label'>{label}</span>
        <span className='urgency-count'>{rotationCount}</span>
        {open ? <MdExpandLess /> : <MdExpandMore />}
      </InteractiveDiv>
      <Collapse in={open}>
        <div>
          {placements.map(placement => (
            <PlacementCard
              key={placement.placementID}
              placement={placement}
              status={status}
              selectedRotation={selectedRotation}
              onSelectRotation={onSelectRotation}
            />
          ))}
        </div>
      </Collapse>
    </div>
  );
};

interface PlacementCardProps {
  placement: PlacementSection;
  status: RotationStatusType;
  selectedRotation?: string;
  onSelectRotation: (key: string | undefined) => void;
}

const PlacementCard = ({
  placement,
  status,
  selectedRotation,
  onSelectRotation
}: PlacementCardProps) => {
  const { adServer, placementName, placementID, platform, lines } = placement;

  return (
    <div className='placement-card'>
      <div className='placement-card-header'>
        <div className='placement-title'>{placementName}</div>
        <div className='placement-meta'>
          {adServer && <span>{formatAdServer(adServer)}</span>}
          <span>#{placementID}</span>
          <span>{platform}</span>
        </div>
      </div>
      <div className='rotation-rows'>
        {lines.map(line => {
          const rotationKey = `${placementID}_${line.isci}`;
          return (
            <RotationRow
              key={line.isci}
              line={line}
              status={status}
              selected={rotationKey === selectedRotation}
              onSelect={() =>
                onSelectRotation(rotationKey === selectedRotation ? undefined : rotationKey)
              }
            />
          );
        })}
      </div>
    </div>
  );
};

interface RotationRowProps {
  line: PlacementSection['lines'][number];
  status: RotationStatusType;
  selected: boolean;
  onSelect: () => void;
}

const RotationRow = ({ line, status, selected, onSelect }: RotationRowProps) => {
  const { isci, pct, date, rotationDates } = line;

  return (
    <InteractiveDiv className={cn('rotation-row', { selected })} onClick={onSelect}>
      <span className='rotation-isci'>{isci}</span>
      <span className='rotation-pct'>{pct}%</span>
      <span className={cn('rotation-when', `when-${status}`)}>{getRelativeBadDay(date)}</span>
      <span className='rotation-dates'>
        <DateSet start={rotationDates.start_date} end={rotationDates.end_date} />
      </span>
      <MdChevronRight className='rotation-chevron' />
    </InteractiveDiv>
  );
};

const ExplainerModal = ({ onClose }: { onClose: () => void }) => (
  <Modal size='lg' show onHide={onClose}>
    <Modal.Header closeButton>
      <Modal.Title>The Invalid Rotation Report</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <p>
        This report surfaces situations either today or in the future where an ISCI will air that is
        not marked as live in the creative map.
      </p>
      <h3>How To Use This Report</h3>
      <ul>
        <li>Each row represents an ISCI that is going to run when it isn&apos;t supposed to.</li>
        <li>Each row is a specific ISCI on a specific Extreme Reach Placement.</li>
        <li>
          The &quot;Bad Day&quot; is the first day when the ISCI is going to run when it isn&apos;t
          supposed to.
        </li>
        <li>
          Click on a row to get more specific information about when the ISCI is going to run in
          Extreme Reach, when the relevant flight is going to be live, and when the ISCI is (and
          isn&apos;t) set to be live in the creative map.
        </li>
        <li>
          The fix for each row will depend on the row, but in general you should go into the
          streaming creative platform and fix the rotation so the non-live ISCI doesn&apos;t run.
        </li>
      </ul>
      <h3>Background</h3>
      <p>
        We know an ISCI will air if there is an Extreme Reach rotation for that ISCI on that tag,
        and there is a flight that uses that tag. We can match all ISCIs set to run in Extreme Reach
        with all current and future flights, we can then see if there are live date ranges in the
        creative map that match that time frame. If there isn&apos;t, then we will have a situation
        where an ISCI will run that won&apos;t be live.
      </p>
      <p>
        This situation is mitigated partially because the streaming creative platform prevents the
        user from setting rotations for non-live creatives. However, a creative might be set to run
        and <em>later</em> become marked as non-live. This situation can happen a few ways. Some
        notable examples:
      </p>
      <ul>
        <li>
          We ran on a network for a week six months ago. We are going back online with that network,
          so we make a flight. Extreme Reach still has the six-month-old creative rotation for that
          tag set to run. If those ISCIs are no longer live, then we&apos;ll run invalid ISCIs.
        </li>
        <li>
          A media specialist enters a creative rotation that will begin two weeks from now. The
          client orders us to halt that creative. The media specialist might remove that ISCI from
          the current rotation, but there was a rotation set to start two weeks from now that was
          put in the system a week ago that won&apos;t be updated.
        </li>
      </ul>
    </Modal.Body>
    <Modal.Footer>
      <Button onClick={onClose}>Close</Button>
    </Modal.Footer>
  </Modal>
);

interface SuppressAllFilteredModalProps {
  lines: { placementID: string; isci: string }[];
  onClose: () => void;
  isFiltered: boolean;
  resetData: () => void;
}

const SuppressAllFilteredModal = ({
  lines,
  onClose,
  isFiltered,
  resetData
}: SuppressAllFilteredModalProps) => {
  const setError = useSetError();
  const [suppressDays, setSuppressDays] = useState(1);
  const [because, setBecause] = useState('');
  const [confirmingSuppress, setConfirmingSuppress] = useState(false);
  const [suppressing, setSuppressing] = useState(false);

  const suppress = useCallback(async () => {
    setSuppressing(true);
    try {
      await CreativeLambdaFetch('/bulk_suppress_rotation_warnings', {
        method: 'post',
        body: { lines, days: suppressDays, because }
      });
      resetData();
      onClose();
    } catch (e) {
      await setError({
        message: `Failed to suppress slack alert. Error: ${e.message}`,
        reportError: e
      });
    }
  }, [lines, setError, suppressDays, because, resetData, onClose]);

  const ourOnClose = useCallback(() => {
    if (!suppressing) {
      onClose();
    }
  }, [onClose, suppressing]);

  return (
    <Modal show onHide={ourOnClose} className='invalid-rotation-report-bulk-suppress-modal'>
      <Modal.Header closeButton>
        <Modal.Title>Bulk Suppress</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          You&apos;re about to suppress warnings for all ISCIs currently listed on this page.{' '}
          {isFiltered ? (
            "Since you have used the filter bar, this will only apply to the lines that haven't been filtered out."
          ) : (
            <strong>
              You have not filtered any lines. Using the filter bar is <em>strongly</em> encouraged.
            </strong>
          )}
        </p>
        <p>
          Please be careful when using this feature. You do not want to accidentally suppress
          warnings for actually problematic rotations.
        </p>
        <div className='suppress-box'>
          <div className='suppress-days'>
            Suppress {lines.length} slack alert{lines.length === 1 ? '' : 's'} for{' '}
            <Form.Control
              className='suppress-days-control'
              size='sm'
              type='number'
              min={0}
              value={suppressDays}
              onChange={e => setSuppressDays(Number(e.target.value))}
            />{' '}
            day{suppressDays === 1 ? '' : 's'} (until{' '}
            {Dfns.format(PRETTY_DATE_FORMAT)(Dfns.addDays(suppressDays)(new Date()))}) because
          </div>
          <Form.Control
            className='because-input'
            as='textarea'
            rows={3}
            value={because}
            onChange={e => setBecause(e.target.value)}
          />
          <Button
            size={Size.LG}
            className='w-100'
            design={Intent.WARNING}
            disabled={suppressing}
            onClick={() => {
              if (confirmingSuppress) {
                suppress();
              } else {
                setConfirmingSuppress(true);
              }
            }}
          >
            {confirmingSuppress ? "Yes, I'm Sure" : 'Suppress Slack Alerts'}
          </Button>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={ourOnClose}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
};
